import * as React from "react";
import { useForm } from "react-hook-form";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

interface FormData {
  name: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  title: string;
  placeholder: string;
  currentName: string;
  isLoading: boolean;
  autoFocus?: boolean;
  /** When set, the submit button shows this text and Cancel is disabled while saving. */
  loadingText?: string;
  /** Resolves when the dialog may close; a rejection is turned into a field error via `getErrorMessage`. */
  onSubmit: (name: string) => Promise<void>;
  getErrorMessage: (error: unknown) => string;
}

export const NameEditDialog = ({
  open,
  onClose,
  title,
  placeholder,
  currentName,
  isLoading,
  autoFocus,
  loadingText,
  onSubmit,
  getErrorMessage,
}: Props) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    setError,
  } = useForm<FormData>({
    defaultValues: { name: currentName },
  });

  React.useEffect(() => {
    if (open) reset({ name: currentName });
  }, [open, reset, currentName]);

  const submit = async ({ name }: FormData) => {
    try {
      await onSubmit(name);
      onClose();
    } catch (error) {
      setError("name", { message: getErrorMessage(error) });
    }
  };

  const showLoading = isLoading && loadingText !== undefined;

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(submit)} className="flex flex-col gap-3">
          <Input
            placeholder={placeholder}
            autoFocus={autoFocus}
            {...register("name", {
              required: "Name is required",
              maxLength: { value: 255, message: "Max 255 characters" },
            })}
          />
          {errors.name && <span className="text-sm text-red-600">{errors.name.message}</span>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={showLoading}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {showLoading ? loadingText : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
